<template>
  <!-- La raíz queda en su lugar (Vue la usa de referencia); solo el indicador se muda a <body>
       (la transición de rutas deja transform en la página y fixed adentro no sirve). -->
  <span class="m-ptr-anchor">
    <div
      ref="indicator"
      class="m-ptr"
      :class="{ 'is-ready': ready, 'is-spinning': spinning }"
      :style="indicatorStyle"
      aria-hidden="true"
    >
      <i v-if="spinning" class="fas fa-sync-alt fa-spin"></i>
      <i v-else class="fas fa-arrow-down" :style="arrowStyle"></i>
    </div>
  </span>
</template>

<script>
/** Distancia (px de dedo) a partir de la cual soltar recarga. */
const THRESHOLD = 70;
/** El indicador baja como mucho esto. */
const MAX_PULL = 110;
/** Aunque la recarga sea instantánea, el giro se ve un momento: si no, parece que no hizo nada. */
const MIN_SPIN_MS = 500;

/**
 * Arrastrar hacia abajo con la página arriba de todo recarga, como en cualquier app.
 * Escucha los toques en todo el documento (en el teléfono el scroll lo hace <body>) y emite
 * «refresh»; `refreshing` (la carga del padre) mantiene el giro hasta que termine.
 */
export default {
  name: "MobilePullToRefresh",
  props: {
    refreshing: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return { pull: 0, startY: null, startX: 0, pulling: false, spinning: false, spinSince: 0 };
  },
  computed: {
    ready() {
      return this.pull >= THRESHOLD;
    },
    indicatorStyle() {
      const y = this.spinning ? THRESHOLD * 0.75 : this.pull * 0.75;
      return {
        transform: `translate(-50%, ${Math.round(y)}px)`,
        opacity: this.spinning ? 1 : Math.min(1, this.pull / (THRESHOLD * 0.6)),
        transition: this.pulling ? "none" : "transform 0.2s ease, opacity 0.2s ease",
      };
    },
    arrowStyle() {
      return { transform: `rotate(${this.ready ? 180 : 0}deg)` };
    },
  },
  watch: {
    refreshing(now) {
      if (!now && this.spinning) this.stopSpin();
    },
  },
  mounted() {
    document.body.appendChild(this.$refs.indicator);
    document.addEventListener("touchstart", this.onStart, { passive: true });
    document.addEventListener("touchmove", this.onMove, { passive: false });
    document.addEventListener("touchend", this.onEnd, { passive: true });
    document.addEventListener("touchcancel", this.onCancel, { passive: true });
  },
  beforeDestroy() {
    document.removeEventListener("touchstart", this.onStart);
    document.removeEventListener("touchmove", this.onMove);
    document.removeEventListener("touchend", this.onEnd);
    document.removeEventListener("touchcancel", this.onCancel);
    const el = this.$refs.indicator;
    if (el && el.parentNode) el.parentNode.removeChild(el);
  },
  methods: {
    atTop() {
      return (document.body.scrollTop || 0) <= 0 && (window.scrollY || 0) <= 0;
    },
    /** Con una hoja, un modal o el menú abiertos el gesto es de ellos. */
    blocked(target) {
      const body = document.body;
      if (this.disabled || this.spinning) return true;
      if (body.classList.contains("m-sheet-open") || body.classList.contains("modal-open")) return true;
      if (document.querySelector(".nav-open")) return true;
      return !!(target && target.closest && target.closest(".m-sheet, .modal, .sidebar, textarea"));
    },
    onStart(e) {
      if (e.touches.length !== 1 || this.blocked(e.target) || !this.atTop()) {
        this.startY = null;
        return;
      }
      this.startY = e.touches[0].clientY;
      this.startX = e.touches[0].clientX;
    },
    onMove(e) {
      if (this.startY === null) return;
      const dy = e.touches[0].clientY - this.startY;
      const dx = e.touches[0].clientX - this.startX;
      if (!this.pulling) {
        // Solo un gesto claramente vertical hacia abajo; lo horizontal (tablas, chips) sigue igual.
        if (dy <= 8 || Math.abs(dx) > dy || !this.atTop()) {
          if (dy < 0 || Math.abs(dx) > 8) this.startY = null;
          return;
        }
        this.pulling = true;
      }
      if (e.cancelable) e.preventDefault();
      // Resistencia: cuanto más se tira, menos baja.
      this.pull = Math.min(MAX_PULL, dy * 0.6);
    },
    onEnd() {
      if (!this.pulling) {
        this.startY = null;
        return;
      }
      const fire = this.ready;
      this.pulling = false;
      this.startY = null;
      this.pull = 0;
      if (!fire) return;
      this.spinning = true;
      this.spinSince = Date.now();
      this.$emit("refresh");
      // Si el padre no llegó a marcar la carga (o fue instantánea), se corta igual.
      this.$nextTick(() => {
        if (!this.refreshing) this.stopSpin();
      });
    },
    onCancel() {
      this.pulling = false;
      this.startY = null;
      this.pull = 0;
    },
    stopSpin() {
      const wait = Math.max(0, MIN_SPIN_MS - (Date.now() - this.spinSince));
      setTimeout(() => {
        this.spinning = false;
      }, wait);
    },
  },
};
</script>

<style scoped>
.m-ptr-anchor {
  display: none;
}
.m-ptr {
  position: fixed;
  top: calc(0.25rem + env(safe-area-inset-top));
  left: 50%;
  z-index: 1040;
  width: 2.5rem;
  height: 2.5rem;
  margin-top: -2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--m-surface);
  border: 1px solid var(--m-border);
  box-shadow: var(--bs-box-shadow);
  color: var(--m-muted);
  pointer-events: none;
}
.m-ptr.is-ready,
.m-ptr.is-spinning {
  color: var(--bs-primary);
}
.m-ptr i {
  transition: transform 0.15s ease;
}
</style>
